import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePageSize from "@/hooks/usePageSize";
import usePopupStore from "@/hooks/usePopupStore";
import useSearchFilters from "@/hooks/useSearchFilters";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import FilterRange from "@/UI/inputs/FilterRange";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import TiersForm from "@/UI/forms/TiersForm";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import SidePopup from "@/UI/popups/SidePopup";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

interface Tier {
    _id: string;
    name: string;
    description: string;
    start: number;
    redeemRate: number;
}

export default function TiersScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const { pageSize, setPageSize } = usePageSize();

    const filters = useSearchFilters({
        search: undefined as string | undefined,
        min_start: undefined as string | undefined,
        max_start: undefined as string | undefined,
        min_redeem_rate: undefined as string | undefined,
        max_redeem_rate: undefined as string | undefined,
    });

    // ── Tiers fetch ─────────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("tiers", { query: { page: 1, limit: pageSize, ...filters.committedQuery } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: pageSize, ...filters.committedQuery } });
        };
        window.addEventListener("tiers:refresh", handler);
        return () => window.removeEventListener("tiers:refresh", handler);
    }, []);

    const tiers: Tier[] = data?.data?.tiers ?? [];
    const total: number = data?.data?.total ?? 0;

    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
        invoke({ query: { page: 1, limit: size, ...filters.committedQuery } });
    };

    // ── Delete fetch ────────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("tiers", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (tier: Tier) => {
        const name = "confirm-delete-tier";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.tiers.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(tier._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Get-one fetch (edit / duplicate) ────────────────────────────────────────
    const pendingModeRef = useRef<"edit" | "duplicate" | null>(null);
    const { loading: fetchingOne, data: oneData, invoke: fetchOne } = useFetch("tiers", { method: "GET" }, false);

    const openTierForm = (mode: "edit" | "duplicate", tier: any) => {
        const name = mode === "edit" ? "edit-tier" : "duplicate-tier";
        const title = mode === "edit" ? t.tierForm.editTitle : t.tierForm.duplicateTitle;
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title,
                Content: TiersForm,
                contentDetails: {
                    tierId: mode === "edit" ? tier._id : undefined,
                    initialData: tier,
                    isDuplicate: mode === "duplicate",
                    onSuccess: () => {
                        invoke({ query: { page: 1, limit: pageSize, ...filters.committedQuery } });
                        setPage(1);
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const wasFetchingOne = useRef(false);
    useEffect(() => {
        if (fetchingOne) {
            wasFetchingOne.current = true;
        } else if (wasFetchingOne.current) {
            wasFetchingOne.current = false;
            const tier = oneData?.data?.tier;
            if (tier && pendingModeRef.current) {
                openTierForm(pendingModeRef.current, tier);
                pendingModeRef.current = null;
            }
        }
    }, [fetchingOne]);

    const handleEdit = (row: Tier) => {
        pendingModeRef.current = "edit";
        fetchOne({ param: row._id });
    };

    const handleDuplicate = (row: Tier) => {
        pendingModeRef.current = "duplicate";
        fetchOne({ param: row._id });
    };

    // ── Add Tier ────────────────────────────────────────────────────────────────
    const openAddTierForm = () => {
        const name = "add-tier";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.tierForm.createTitle,
                Content: TiersForm,
                contentDetails: {
                    onSuccess: () => {
                        window.dispatchEvent(new CustomEvent("tiers:refresh"));
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Columns ─────────────────────────────────────────────────────────────────
    const columns: TableColumn<Tier>[] = [
        {
            key: "name",
            header: t.tiers.col.name,
            width: "200px",
            render: (row) => <TableCell.Text value={row.name || "—"} maxChars={30} />,
        },
        {
            key: "description",
            header: t.tiers.col.description,
            width: "250px",
            render: (row) => <TableCell.Text value={row.description || "—"} maxChars={40} />,
        },
        {
            key: "start",
            header: t.tiers.col.start,
            width: "120px",
            render: (row) => (
                <span className="tiers-points-badge">
                    {row.start.toLocaleString()} {(t.tiers as any).beans as string}
                </span>
            ),
        },
        {
            key: "redeemRate",
            header: t.tiers.col.redeemRate,
            width: "160px",
            render: (row) => (
                <div className="tiers-redeem-rate">
                    <span>
                        {row.redeemRate} {t.challenges.beans}
                    </span>
                    <span className="tiers-redeem-arrow">{lang === "ar" ? "←" : "→"}</span>
                    <span>1 {t.orders.sar}</span>
                </div>
            ),
        },
        {
            key: "actions",
            header: t.tiers.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id || fetchingOne}
                    actions={[
                        { name: "edit", icon: "/icons/edit-icon.svg", handler: () => handleEdit(row) },
                        { name: "duplicate", icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(row) },
                        { name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true },
                    ]}
                />
            ),
        },
    ];

    const filterContent = (
        <>
            <FilterSearchInput
                value={filters.draft.search}
                onChange={(v) => filters.setDraftField("search", v)}
                placeholder={t.filters.tiers.search}
            />
            <FilterRange
                label={t.filters.tiers.startRange}
                fromValue={filters.draft.min_start}
                toValue={filters.draft.max_start}
                onFromChange={(v) => filters.setDraftField("min_start", v)}
                onToChange={(v) => filters.setDraftField("max_start", v)}
                fromPlaceholder={t.filters.from}
                toPlaceholder={t.filters.to}
            />
            <FilterRange
                label={t.filters.tiers.redeemRange}
                fromValue={filters.draft.min_redeem_rate}
                toValue={filters.draft.max_redeem_rate}
                onFromChange={(v) => filters.setDraftField("min_redeem_rate", v)}
                onToChange={(v) => filters.setDraftField("max_redeem_rate", v)}
                fromPlaceholder={t.filters.from}
                toPlaceholder={t.filters.to}
            />
        </>
    );

    return (
        <div className="tiers-screen">
            <ActionsBar
                onAdd={openAddTierForm}
                filterContent={filterContent}
                filterOpen={filters.filterOpen}
                onFilterOpen={filters.openFilter}
                onFilterSave={() =>
                    filters.saveFilter((q) => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize, ...q } });
                    })
                }
                onFilterClear={() =>
                    filters.clearFilter(() => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize } });
                    })
                }
                onFilterCancel={filters.cancelFilter}
                hasActiveFilters={filters.hasActiveFilters}
            />
            <div className="tiers-screen-content">
                <Table
                    columns={columns}
                    data={tiers}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize, total, onPageChange: setPage, onPageSizeChange: handlePageSizeChange }}
                />
            </div>
        </div>
    );
}
